import { gql } from '@apollo/client';
import { createLogger } from '../utils/createLogger';
import { AppPatient } from '../types';
import { filterLogContext } from '../utils/filterLogContext';
import { graphQlClient } from '../utils/graphQlClient';

const logger = createLogger('updateEligibilityContext');
export interface UpdateEligibilityContextInputV2 {
  encounterId: string;
  selectedProvider: {
    npi: string;
    firstName: string;
    lastName: string;
  };
  encryptedPatientDetails?: {
    encryptedIdentifyInput?: string;
  };
  anonymizedPatient?: string;
}

const UPDATE_ELIGIBILITY_CONTEXT_QUERY_GQL = gql`
  query UpdateEligibilityContextV2($input: UpdateEligibilityContextInputV2!) {
    updateEligibilityContextV2(input: $input) {
      transactionId
    }
  }
`;

const updateEligibilityContextGQL = async (
  input: UpdateEligibilityContextInputV2,
  insurer?: string,
) => {
  const gqlClient = graphQlClient.getClient();
  const { data } = await gqlClient.query<{ updateEligibilityContextV2: { transactionId: string } }>(
    {
      query: UPDATE_ELIGIBILITY_CONTEXT_QUERY_GQL,
      variables: { input },
      context: {
        uri: '/prior-auth/api/graphql',
        ...(insurer && {
          headers: {
            'x-insurer': insurer,
          },
        }),
      },
    },
  );
  return data?.updateEligibilityContextV2?.transactionId;
};

export const updateEligibilityContext = async ({
  patient,
  provider,
  encounterId,
}: {
  patient: AppPatient;
  provider: {
    npi: string;
    firstName: string;
    lastName: string;
  };
  encounterId: string;
}): Promise<string | undefined> => {
  const logContext = filterLogContext({ patient, selectedProvider: provider });

  if (!patient?.patientSourceVaultToken) {
    logger.warning('No member token found', {
      ...logContext,
      encounterId,
      noPHI: true,
    });
    return;
  }
  logger.info('Update eligibility context', {
    ...logContext,
    encounterId,
    noPHI: true,
  });
  return await updateEligibilityContextGQL(
    {
      encounterId,
      selectedProvider: {
        npi: provider.npi,
        firstName: provider?.firstName,
        lastName: provider?.lastName,
      },
      anonymizedPatient: patient?.patientSourceVaultToken,
    },
    patient?.insurance?.insurer,
  );
};
